import { toast } from 'react-toastify';
import { JAVA_API_STATUS } from '../../utils/axios-interceptor';
import {
    AddCompanyToScanPayload,
    AddCompanyToWatchlistPayload,
    addToWatchlistService,
    archiveDocumentService,
    composeDocumentService,
    deleteDocumentService,
    fetchOrgInfoService,
    fetchOrgUser,
    getAllTaskTrackerDataService,
    getApplicationFormTemplateService,
    getBureauKycConsentTemplateService,
    getDocumentHistoryService,
    getDocumentService,
    unarchiveDocumentService,
    getEngagementLetterTemplateService,
    getNDATemplateService,
    getInvestorConfig,
    inviteCompanyUserService,
    listDocumentsService,
    moveCompanyToNewTierService,
    saveDocumentService,
    unlockAicaForCompanyService,
    UnlockAICAReportPayload,
} from './service';
import { Handlers } from '../../types/actions/types';
import { FetchInvestorConfigPayload } from './action';

export async function addCompanyToWatchlist(payload: AddCompanyToWatchlistPayload, handlers) {
    try {
        const response = await addToWatchlistService(payload);
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                handlers?.onSuccess?.(response?.data);
                return;
            case JAVA_API_STATUS.ERROR:
                handlers?.onError?.();
                return toast.error(responseData?.responseMessage || 'Unable to add. Please try again later.');
        }
    } catch (err) {
        handlers?.onError();
        toast.warn('Unable to add. Please try again later.');
    }
}

export async function moveCompanyToNewTier(payload: AddCompanyToScanPayload, handlers: Handlers = {}) {
    try {
        const response = await moveCompanyToNewTierService(payload);
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                handlers?.onSuccess?.();
                return;
            case JAVA_API_STATUS.ERROR:
            default:
                handlers?.onError?.();
                throw new Error(responseData.responseMessage || 'Some error occoured');
        }
    } catch (err) {
        handlers?.onError?.();
        throw err;
    }
}

export async function inviteCompanyUser(payload: AddCompanyToScanPayload, handlers: Handlers) {
    try {
        const response = await inviteCompanyUserService(payload);
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                handlers?.onSuccess?.();
                return;
            case JAVA_API_STATUS.ERROR:
                handlers?.onError?.();
                return toast.error(responseData?.responseMessage || 'Some error occoured');
        }
    } catch (err) {
        toast.warn('Unable to add. Please try again later.');
    }
}

export async function unlockOrRefreshReport(payload: UnlockAICAReportPayload, handlers: Handlers) {
    try {
        const response = await unlockAicaForCompanyService(payload);
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                handlers?.onSuccess?.();
                return;
            case JAVA_API_STATUS.ERROR:
                handlers?.onError?.();
                return toast.error(responseData?.responseMessage || 'Some error occoured');
        }
    } catch (err) {
        handlers?.onError?.();
        toast.warn('Something went wrong');
    }
}

export async function fetchConfigByConfigName(payload: FetchInvestorConfigPayload) {
    try {
        const response = await getInvestorConfig(payload);
        const responseData = response.data?.responseData;

        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                return response.data?.mainConfig;
            case JAVA_API_STATUS.ERROR:
            default:
                return null;
        }
    } catch (err) {
        console.error('Error in fetching config: ', payload.configName, '\nError: ', err);
    }
}

export async function fetchUserFromOrgId(orgId: string, accountType: string, noToast = false) {
    try {
        const response = await fetchOrgUser({ orgId, accountType });
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                return response.data?.userDetails;
            case JAVA_API_STATUS.ERROR:
                if (!noToast) toast.error(responseData?.responseMessage || 'Some error occoured');
            default:
                return null;
        }
    } catch (err) {
        console.error('Error in fetching user for orgId: ', orgId, ' accountType: ', accountType, '\nError: ', err);
    }
}

export async function getAllTaskTrackerData(payload: { orgId: string }, handlers: Handlers) {
    try {
        const response = await getAllTaskTrackerDataService(payload);
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                handlers?.onSuccess?.(response.data?.responseBody?.taskTrackerData ?? {});
                return;
            case JAVA_API_STATUS.ERROR:
                handlers?.onError?.(responseData?.responseMessage || 'Failed to fetch task tracker data');
                return toast.error(responseData?.responseMessage || 'Some error occoured');
        }
    } catch (err) {
        // Pass a safe error object to onError handler to avoid accessing undefined properties
        handlers?.onError?.(err instanceof Error ? err : new Error('Failed to fetch task tracker data'));
    }
}

// Engagement Letter
export async function fetchEngagementLetterTemplate(payload: { accountType: string; pnoId: string; orgId: string; primaryEmail: string }) {
    try {
        const response = await getEngagementLetterTemplateService(payload);
        const responseData = response?.data?.responseData;
        if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return response?.data?.mainConfig?.additionalDetailsConfig?.engagementLetterTemplate || [];
        } else {
            throw new Error('Error in fetching engagement letter template: ' + JSON.stringify(responseData || {}));
        }
    } catch (error) {
        console.error('Error in fetching engagement letter template', error);
        return [];
    }
}

export async function fetchBureauKycConsentTemplate(payload: {
    accountType: string;
    assesseeOrgId: string;
    investorOrgId: string;
    primaryEmail: string;
}) {
    try {
        const response = await getBureauKycConsentTemplateService(payload);
        const responseData = response?.data?.responseData;
        if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return response?.data?.mainConfig?.additionalDetailsConfig?.bureauKycConsentTemplate || [];
        } else {
            throw new Error('Error in fetching bureau kyc consent template: ' + JSON.stringify(responseData || {}));
        }
    } catch (error) {
        console.error('Error in fetching bureau kyc consent template', error);
        return [];
    }
}

// NDA
export async function fetchNDATemplate(payload: { accountType: string; pnoId: string; orgId: string; primaryEmail: string }) {
    try {
        const response = await getNDATemplateService(payload);
        const responseData = response?.data?.responseData;
        if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return response?.data?.mainConfig?.additionalDetailsConfig?.ndaTemplate || [];
        } else {
            throw new Error('Error in fetching NDA template: ' + JSON.stringify(responseData || {}));
        }
    } catch (error) {
        console.error('Error in fetching NDA template', error);
        return [];
    }
}

// Application Form
export async function fetchApplicationFormTemplate(payload: {
    accountType: string;
    pnoId: string;
    orgId: string;
    primaryEmail: string;
    userRole?: string;
}) {
    try {
        const response = await getApplicationFormTemplateService(payload);
        const responseData = response?.data?.responseData;
        if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return response?.data?.mainConfig?.additionalDetailsConfig?.applicationFormTemplate || [];
        } else {
            throw new Error('Error in fetching Application Form template: ' + JSON.stringify(responseData || {}));
        }
    } catch (error) {
        console.error('Error in fetching Application Form template', error);
        return [];
    }
}

export async function fetchOrgInfoById(payload: { orgId: string }) {
    try {
        const response = await fetchOrgInfoService(payload);
        const responseData = response?.data?.responseData;
        if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return response?.data?.orgInfo || {};
        } else {
            throw new Error('Error in fetching org info by id: ' + JSON.stringify(responseData || {}));
        }
    } catch (error) {
        console.error('Error in fetching org info by id ', error);
        return {};
    }
}

// Generated Documents (Config-Driven Reports Engine)

export async function listDocuments(payload: { assesseeOrgId: string; includeArchived?: boolean }): Promise<any[]> {
    const response = await listDocumentsService(payload);
    const body = response?.data;
    const responseData = body?.responseData;
    if (responseData?.responseCode === JAVA_API_STATUS.ERROR) {
        throw new Error(responseData?.responseMessage || 'Failed to load documents');
    }
    // aica wraps the list under `data.documents`. Checking `body?.data` (the wrapper
    // object) before `data.documents` would short-circuit and return a non-array,
    // breaking the `.map` in fetchReports.
    const docs = body?.data?.documents ?? body?.documents ?? body?.responseBody?.documents ?? [];
    return Array.isArray(docs) ? docs : [];
}

export interface GenerateDocumentResult {
    /** Plate node array — present when formatType is DOC. */
    document: any[] | null;
    /** FortuneSheet workbook array — present when formatType is XLSX. */
    workbook: any[] | null;
}

export async function generateDocumentFromTemplate(payload: {
    assesseeOrgId: string;
    documentType: string;
    [key: string]: any;
}): Promise<GenerateDocumentResult> {
    const response = await composeDocumentService(payload);
    const body = response?.data;
    const responseData = body?.responseData;
    const rb = body?.responseBody ?? body;
    const document: any[] | null = rb?.document ?? body?.data?.document ?? null;
    const workbook: any[] | null = rb?.workbook ?? body?.data?.workbook ?? null;
    const isEmptyResult = (!document || document.length === 0) && (!workbook || workbook.length === 0);
    if (responseData?.responseCode === JAVA_API_STATUS.ERROR || rb?._composeFailed === true || isEmptyResult) {
        throw new Error(responseData?.responseMessage || 'Failed to generate document from template');
    }
    return { document, workbook };
}

export async function saveDocument(payload: { assesseeOrgId: string; [key: string]: any }): Promise<any | null> {
    const response = await saveDocumentService(payload);
    const body = response?.data;
    const responseData = body?.responseData;
    // Throw on an error envelope (endpoints return HTTP 200 with an in-body
    // responseCode). A silent null makes autosave show "Saved" while dropping the
    // edit — and since the caller never latches the server-assigned id, the next
    // save sends no id and the backend mints a duplicate lineage (RECUR-26249).
    if (responseData?.responseCode === JAVA_API_STATUS.ERROR) {
        throw new Error(responseData?.responseMessage || 'Failed to save document');
    }
    return body?.responseBody?.document ?? body?.document ?? body?.data?.document ?? null;
}

/**
 * Loads one document. `docId` targets an exact row; `genId` a lineage's newest
 * live row; `documentType` the org's newest live doc of that type (autosave
 * draft preferred — even one whose lineage was never committed — else the
 * newest committed version). The documentType form is how the document drawers
 * (One Pager, EL, NDA, …) open their doc. Resolves null when nothing exists.
 */
export async function getDocument(payload: {
    assesseeOrgId: string;
    docId?: string;
    genId?: string;
    documentType?: string;
}): Promise<any | null> {
    const response = await getDocumentService(payload);
    const body = response?.data;
    return body?.responseBody?.document ?? body?.document ?? body?.data?.document ?? null;
}

export interface DocumentVersion {
    id?: string;
    _id?: string;
    genId?: string;
    versionNumber?: number;
    name?: string;
    createdAt?: number;
    lastUpdatedAt?: number;
    updatedBy?: string;
    lastEditedBy?: string;
    createdBy?: string;
    isDraft?: boolean;
    status?: string;
}

export async function getDocumentHistory(payload: { assesseeOrgId: string; genId: string }): Promise<DocumentVersion[]> {
    const response = await getDocumentHistoryService(payload);
    const body = response?.data;
    const responseData = body?.responseData;
    if (responseData?.responseCode === JAVA_API_STATUS.ERROR) {
        throw new Error(responseData?.responseMessage || 'Failed to load version history');
    }
    const versions = body?.data?.versions ?? body?.versions ?? body?.responseBody?.versions ?? [];
    return Array.isArray(versions) ? versions : [];
}

// delete/archive/unarchive share the same {genId, assesseeOrgId} → data:{genId, affectedVersions} shape.
async function mutateDocumentLifecycle(
    service: (p: { assesseeOrgId: string; genId: string }) => Promise<any>,
    payload: { assesseeOrgId: string; genId: string },
    action: string,
): Promise<void> {
    const response = await service(payload);
    const responseData = response?.data?.responseData;
    if (responseData?.responseCode !== JAVA_API_STATUS.SUCCESS) {
        throw new Error(responseData?.responseMessage || `Failed to ${action} document`);
    }
}

export function deleteDocument(payload: { assesseeOrgId: string; genId: string }): Promise<void> {
    return mutateDocumentLifecycle(deleteDocumentService, payload, 'delete');
}

export function archiveDocument(payload: { assesseeOrgId: string; genId: string }): Promise<void> {
    return mutateDocumentLifecycle(archiveDocumentService, payload, 'archive');
}

export function unarchiveDocument(payload: { assesseeOrgId: string; genId: string }): Promise<void> {
    return mutateDocumentLifecycle(unarchiveDocumentService, payload, 'unarchive');
}
